import * as React from "react";

/**
 * Detects if the mouse is hovering over an element.
 *
 * Both when the mouse transits an existing component, and when a new component mounts under the mouse.
 *
 * return
 *  - `ref`: A React ref to attach to the DOM element you want to monitor.
 *  - `isHovering`: true if the element is being hovered, false otherwise.
 */
export function useHover<T extends HTMLElement>(): [
  React.MutableRefObject<T | null>,
  boolean,
] {
  const [isHovering, setIsHovering] = React.useState<boolean>(false);
  const ref = React.useRef<T | null>(null);

  React.useEffect(function attachHoverListeners() {
    const abort = new AbortController();
    function dispose() {
      abort.abort();
    }

    const node = ref.current;
    if (!node) {
      return dispose;
    }

    // Standard event listeners
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    node.addEventListener("mouseenter", handleMouseEnter, {
      signal: abort.signal,
    });
    node.addEventListener("mouseleave", handleMouseLeave, {
      signal: abort.signal,
    });

    // On mount, check if the mouse is *already* over the element's position.
    document.addEventListener(
      "mousemove",
      function checkIsHovering(event: MouseEvent) {
        const rect = node.getBoundingClientRect();
        const isMouseOverRect =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (isMouseOverRect) {
          setIsHovering(isMouseOverRect);
        }
      },
      {
        once: true,
        signal: abort.signal,
      },
    );

    return dispose;
  }, []);

  return [ref, isHovering];
}
